/**
 * Beste Talk — il telefono dell'azienda, app a se' (2026-11-02).
 *
 * Cosimo: *«COSTRUISCI UNA APP chiamata Beste Talk che per ora faccia fare solo chiamate via voip
 * tra interni senza trunk sip ne collegamenti ai cordless»*, *«Quando attivo la funzionalità alla
 * persona devo potergli affidare l'interno di 3 numeri»*, e *«ATTENZIONE ASSOLUTA ALLA PARTE DI
 * NOTIFICA. IL TELEFONO DEVE SUONARE SEMPRE. FAI ANCHE DEI LOG LATO SERVER DELLE CHIAMATE»*.
 *
 * Qui stanno le regole che server, app e Cruscotto devono leggere uguali: gli stati di una
 * chiamata e chi li puo' cambiare, i tempi dello squillo, la forma dell'interno, gli eventi del
 * registro. Sono funzioni pure, provate da `scripts/prova-talk.mjs`.
 */
import { dayKey, oraDiRoma } from './format.js';
/** Gli stati di una chiamata. `squilla` e `in_corso` sono **vivi**; gli altri sono finali. */
export const STATI_TALK = [
    'squilla',
    'in_corso',
    'conclusa',
    'rifiutata',
    'annullata',
    'persa',
    'occupato',
];
export const STATI_TALK_VIVI = ['squilla', 'in_corso'];
export function chiamataViva(stato) {
    return STATI_TALK_VIVI.includes(stato);
}
/**
 * Lo stato che un gesto produce, o il perche' non si puo'. **L'unica tavola delle transizioni**:
 * il servizio la legge prima di scrivere, e scrive con un `updateMany` condizionato allo stato di
 * partenza, cosi' due gesti che arrivano insieme non si sovrascrivono.
 *
 * - `rispondi`: solo il chiamato, solo mentre squilla e prima della scadenza → `in_corso`;
 * - `rifiuta`: solo il chiamato, solo mentre squilla → `rifiutata`;
 * - `annulla`: solo il chiamante, solo mentre squilla → `annullata`;
 * - `chiudi`: uno dei due, mentre e' in corso → `conclusa`; mentre squilla vale come `annulla`
 *   per il chiamante e `rifiuta` per il chiamato (chi preme il tasto rosso non deve sapere in che
 *   stato e' la chiamata).
 */
export function transizioneTalk(stato, gesto, ruolo, scaduta) {
    if (!chiamataViva(stato))
        return { ok: false, motivo: 'finita' };
    if (ruolo === 'ospite')
        return { ok: false, motivo: 'non_tuo' };
    switch (gesto) {
        case 'rispondi':
            if (ruolo !== 'chiamato')
                return { ok: false, motivo: 'non_tuo' };
            if (stato !== 'squilla')
                return { ok: false, motivo: 'finita' };
            if (scaduta)
                return { ok: false, motivo: 'scaduta' };
            return { ok: true, nuovo: 'in_corso' };
        case 'rifiuta':
            if (ruolo !== 'chiamato')
                return { ok: false, motivo: 'non_tuo' };
            if (stato !== 'squilla')
                return { ok: false, motivo: 'finita' };
            return { ok: true, nuovo: 'rifiutata' };
        case 'annulla':
            if (ruolo !== 'chiamante')
                return { ok: false, motivo: 'non_tuo' };
            if (stato !== 'squilla')
                return { ok: false, motivo: 'finita' };
            return { ok: true, nuovo: 'annullata' };
        case 'chiudi':
            if (stato === 'squilla')
                return { ok: true, nuovo: ruolo === 'chiamante' ? 'annullata' : 'rifiutata' };
            return { ok: true, nuovo: 'conclusa' };
    }
}
/**
 * Il posto di una persona dentro una chiamata (`talk_call_members.stato`). Chiamante e chiamato
 * hanno il loro posto dalla nascita della chiamata; un ospite lo riceve quando qualcuno lo aggiunge.
 *
 * - `squilla`: il suo telefono sta suonando (il chiamato prima della risposta, un ospite invitato);
 * - `dentro`: e' nella chiamata (il chiamante da subito, gli altri dalla risposta);
 * - `uscito`: c'era ed e' uscito, o la chiamata e' finita mentre c'era;
 * - `rifiutato`, `perso`, `annullato`: il suo squillo e' finito senza che entrasse.
 */
export const STATI_MEMBRO_TALK = ['squilla', 'dentro', 'uscito', 'rifiutato', 'perso', 'annullato'];
export function membroVivo(stato) {
    return stato === 'squilla' || stato === 'dentro';
}
/**
 * Lo stato che un gesto dell'**ospite** produce sul suo posto, o il perche' non si puo'. Come la
 * tavola della chiamata, il servizio scrive con un `updateMany` condizionato allo stato di partenza.
 *
 * - `rispondi`: solo mentre il suo telefono squilla, prima della scadenza, e solo se la chiamata e'
 *   ancora in corso → `dentro`;
 * - `rifiuta`: mentre squilla → `rifiutato`;
 * - `esci` (il tasto rosso): mentre squilla vale come `rifiuta`, da dentro → `uscito`.
 */
export function transizioneMembro(statoMembro, gesto, chiamataInCorso, scaduto) {
    if (!membroVivo(statoMembro) || !chiamataInCorso)
        return { ok: false, motivo: 'finita' };
    switch (gesto) {
        case 'rispondi':
            if (statoMembro !== 'squilla')
                return { ok: false, motivo: 'finita' };
            if (scaduto)
                return { ok: false, motivo: 'scaduta' };
            return { ok: true, nuovo: 'dentro' };
        case 'rifiuta':
            if (statoMembro !== 'squilla')
                return { ok: false, motivo: 'finita' };
            return { ok: true, nuovo: 'rifiutato' };
        case 'esci':
            return { ok: true, nuovo: statoMembro === 'squilla' ? 'rifiutato' : 'uscito' };
    }
}
/**
 * Quando qualcuno esce da una chiamata **in corso**, la chiamata continua se dentro restano almeno
 * due persone; altrimenti finisce, e chi stava ancora squillando smette. Una chiamata a due resta
 * quindi quella di sempre: uno chiude, finisce per tutti e due.
 */
export function esitoUscita(dentroRimasti) {
    return dentroRimasti >= 2 ? 'continua' : 'conclusa';
}
/**
 * Il tipo di una chiamata (§5 noviens tricies, 2026-11-03):
 *
 * - `voce`: la telefonata di sempre, fra persone (anche verso un numero di gruppo);
 * - `annuncio`: l'interfono verso un gruppo. Chi annuncia parla tenendo premuto; gli altri **ascoltano
 *   e basta** (il motore glielo fa rispettare: il loro gettone non pubblica). Finisce quando chi
 *   annuncia esce;
 * - `citofono`: il campanello di una porta. Non c'e' una persona che chiama ma una porta, e chi
 *   risponde ha il tasto «Apri». Finche' alla porta non c'e' un apparecchio con microfono, la
 *   chiamata non ha voce.
 */
export const TIPI_CHIAMATA_TALK = ['voce', 'annuncio', 'citofono'];
/**
 * Perche' un posto squilla dentro una chiamata gia' in corso, oltre all'invito («aggiungi»):
 *
 * - `cieco`: il trasferimento senza parlare prima. Chi passa esce **subito**; chi era con lui resta
 *   in attesa e sente lo squillo; se il nuovo non risponde la chiamata **torna** a chi l'ha passata;
 * - `consulto`: il trasferimento parlando prima. Chi passa mette in attesa gli altri (il server li
 *   esclude davvero dalla voce, non solo l'app), parla col nuovo e poi «Passa» o «Torna»;
 * - `ritorno`: la chiamata che torna a chi l'aveva passata o parcheggiata, perche' nessuno l'ha presa;
 * - `parcheggio`: chi riprende una chiamata parcheggiata;
 * - `richiamata`: chi aveva premuto «Richiamami» e ora squilla, perche' la persona cercata si e' liberata
 *   e ha gia' risposto (vedi `TalkRichiamata`).
 */
export const PASSAGGI_TALK = ['cieco', 'consulto', 'ritorno', 'parcheggio', 'richiamata'];
/**
 * Quando qualcuno esce da una chiamata **in corso**: continua o finisce? La tavola di `esitoUscita`
 * allargata alle funzioni del centralino.
 *
 * - **voce**: continua con almeno due persone dentro, come sempre; con **una** sola continua solo se
 *   la chiamata e' **parcheggiata** o se sta squillando un passaggio (il cieco, il ritorno): chi e'
 *   rimasto aspetta che qualcuno la prenda, non resta appeso al nulla;
 * - **annuncio**: continua finche' dentro c'e' chi annuncia, e finisce quando esce lui;
 * - **citofono**: continua finche' dentro c'e' qualcuno (chi ha risposto alla porta).
 */
export function esitoUscitaTalk(s) {
    if (s.tipo === 'annuncio')
        return s.annunciatoreDentro && s.dentroRimasti >= 1 ? 'continua' : 'conclusa';
    if (s.tipo === 'citofono')
        return s.dentroRimasti >= 1 ? 'continua' : 'conclusa';
    if (s.dentroRimasti >= 2)
        return 'continua';
    if (s.dentroRimasti === 1 && (s.parcheggiata || s.passaggioCheSquilla))
        return 'continua';
    return 'conclusa';
}
export const ORA_HHMM = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
/** Una fascia scritta bene: giorni 1-7 senza ripetizioni, ore `HH:MM`, inizio diverso dalla fine. */
export function fasciaValida(f) {
    if (!f || typeof f !== 'object')
        return false;
    const x = f;
    if (!Array.isArray(x['giorni']) || x['giorni'].length === 0 || x['giorni'].length > 7)
        return false;
    if (!x['giorni'].every((g) => Number.isInteger(g) && g >= 1 && g <= 7))
        return false;
    if (new Set(x['giorni']).size !== x['giorni'].length)
        return false;
    return typeof x['da'] === 'string' && typeof x['a'] === 'string' && ORA_HHMM.test(x['da']) && ORA_HHMM.test(x['a']) && x['da'] !== x['a'];
}
/** Il giorno della settimana a Roma, 1 = lunedi' … 7 = domenica. */
function giornoDiRoma(adesso) {
    const g = new Date(`${dayKey(adesso)}T00:00:00Z`).getUTCDay();
    return g === 0 ? 7 : g;
}
/** La fascia vale adesso? Ore e giorni di Roma. */
export function inFascia(f, adesso) {
    const oggi = giornoDiRoma(adesso);
    const ieri = oggi === 1 ? 7 : oggi - 1;
    const ora = oraDiRoma(adesso);
    if (f.da < f.a)
        return f.giorni.includes(oggi) && ora >= f.da && ora < f.a;
    // Passa la mezzanotte: la sera del giorno della fascia, o la mattina dopo.
    return (f.giorni.includes(oggi) && ora >= f.da) || (f.giorni.includes(ieri) && ora < f.a);
}
/**
 * Il non disturbare e' acceso **adesso**? Acceso a mano (fino a un'ora scelta, o finche' non lo si
 * spegne), oppure dentro una delle fasce a orario.
 */
export function nonDisturbareAttivo(r, adesso) {
    if (r.ndAttivo) {
        const fino = r.ndFino === null ? null : new Date(r.ndFino);
        if (fino === null || fino.getTime() > adesso.getTime())
            return true;
    }
    const fasce = Array.isArray(r.ndOrario) ? r.ndOrario.filter(fasciaValida) : [];
    return fasce.some((f) => inFascia(f, adesso));
}
/**
 * Dove puo' andare un inoltro: un interno, un numero di gruppo o (quando arrivera' la linea esterna,
 * §92/§93) un numero esterno. Il server accetta oggi solo i primi due e risponde `esterne_non_attive`
 * al terzo, come la tastiera.
 */
export function destinazioneInoltroValida(numero) {
    return numeroInternoValido(numero) || numeroEsternoValido(numero);
}
/** Quante volte al massimo un inoltro ne segue un altro prima di fermarsi (e il giro chiuso: A → B → A). */
export const INOLTRI_MASSIMI = 3;
/**
 * Le **frasi vocali** di Beste Talk (Cosimo, 2026-11-03: *«METTI ANCHE I MESSAGGI A VOCE ALL'INTERNO DEL
 * PROGRAMMA IN MODO CHE SI CAPISCA COSA FARE. BELLI SINTETICI MA COMPRENSIBILI E CHE TI GUIDINO IN BASE
 * AL TELEFONO CHE STAI UTILIZZANDO PER CHIAMARE»*).
 *
 * Una frase per momento, scritta per ogni telefono: sull'app si **tocca** un tasto a schermo, nel
 * Cruscotto lo si **clicca**, sul cordless si **preme** un tasto della tastiera (il cancelletto per la
 * richiamata). La voce e' registrata in casa una volta (sintesi vocale libera, `tools/voce-talk/`), e
 * l'app la suona dal telefono stesso, nell'audio della chiamata. Il testo qui e' l'unica fonte: i file
 * si rifanno da qui, e la schermata scrive le stesse parole che si sentono.
 *
 * Senza nomi dentro: una frase registrata non puo' sapere chi stai chiamando, e il nome e' gia' scritto
 * grande sullo schermo.
 */
export const FRASI_TALK = {
    /** Chi chiamo e' al telefono. */
    occupato: {
        app: 'È al telefono. Tocca Richiamami: vi metto in contatto appena si libera.',
        browser: 'È al telefono. Clicca Richiamami: vi metto in contatto appena si libera.',
        cordless: 'È al telefono. Premi cancelletto: vi metto in contatto appena si libera.',
    },
    /** Chi chiamo ha il «Non disturbare». */
    nonDisturbare: {
        app: 'Ora non può rispondere. Tocca Richiamami: vi metto in contatto appena è disponibile.',
        browser: 'Ora non può rispondere. Clicca Richiamami: vi metto in contatto appena è disponibile.',
        cordless: 'Ora non può rispondere. Premi cancelletto: vi metto in contatto appena è disponibile.',
    },
    /** Il gruppo che chiamo ha tutti al telefono. */
    gruppoOccupato: {
        app: 'Sono tutti al telefono. Tocca Richiamami: vi metto in contatto appena uno si libera.',
        browser: 'Sono tutti al telefono. Clicca Richiamami: vi metto in contatto appena uno si libera.',
        cordless: 'Sono tutti al telefono. Premi cancelletto: vi metto in contatto appena uno si libera.',
    },
    /** «Richiamami» preso. */
    prenotata: {
        app: 'Fatto. Appena si libera, ti chiamo io.',
        browser: 'Fatto. Appena si libera, ti chiamo io.',
        cordless: 'Fatto. Appena si libera, ti chiamo io. Puoi riagganciare.',
    },
    /** A chi era cercato, dopo che ha risposto alla richiamata: aspetta che squilli a chi lo cercava. */
    richiamataInLinea: {
        app: 'Ti cercavano. Resta in linea: ti metto in contatto.',
        browser: 'Ti cercavano. Resta in linea: ti metto in contatto.',
        cordless: 'Ti cercavano. Resta in linea: ti metto in contatto.',
    },
    /** A chi era cercato: chi lo cercava non risponde piu'. */
    richiamataNonRisponde: {
        app: 'Chi ti cercava ora non risponde. Puoi riagganciare.',
        browser: 'Chi ti cercava ora non risponde. Puoi riagganciare.',
        cordless: 'Chi ti cercava ora non risponde. Puoi riagganciare.',
    },
    /** A chi viene passato con un trasferimento cieco: aspetta il collega. */
    tiPasso: {
        app: 'Ti passo a un collega. Resta in linea.',
        browser: 'Ti passo a un collega. Resta in linea.',
        cordless: 'Ti passo a un collega. Resta in linea.',
    },
    /** A chi e' tenuto in attesa (consulto, parcheggio). */
    inAttesa: {
        app: 'Sei in attesa. Resta in linea.',
        browser: 'Sei in attesa. Resta in linea.',
        cordless: 'Sei in attesa. Resta in linea.',
    },
    /** Il collega a cui ero passato non ha risposto: la chiamata torna indietro. */
    nessunoHaPreso: {
        app: 'Il collega non ha risposto. Resta in linea.',
        browser: 'Il collega non ha risposto. Resta in linea.',
        cordless: 'Il collega non ha risposto. Resta in linea.',
    },
    /** Chi risponde al citofono. */
    citofono: {
        app: 'Citofono. Tocca Apri per aprire la porta.',
        browser: 'Citofono. Clicca Apri per aprire la porta.',
        cordless: 'Citofono. Premi cancelletto per aprire la porta.',
    },
    /**
     * La linea e' muta da qualche secondo (4/11, Cosimo: «METTI UN CUE AUDIO CHE FA CAPIRE CHE NON C'È
     * COMUNICAZIONE NEL PERIODO IN CUI NON C'È. PER ENTRAMBI GLI INTERLOCUTORI»). La dicono tutti e due i
     * telefoni, ognuno da se': quello senza rete e quello che non sente piu' l'altro. Fra una frase e l'altra
     * il doppio bip di linea assente (`segnaleLinea`).
     */
    lineaAssente: {
        app: 'La linea è disturbata. Resta in linea: sto ricollegando.',
        browser: 'La linea è disturbata. Resta in linea: sto ricollegando.',
        cordless: 'La linea è disturbata. Resta in linea: sto ricollegando.',
    },
};
/**
 * **La risposta rapida** (4/11, Cosimo): mentre squilla, invece di rispondere, si rifiuta con una frase che
 * arriva subito a chi chiama — nella sua schermata di fine chiamata («Marco: Ti richiamo tra 5 minuti») e
 * come notifica se ha gia' chiuso. Tre frasi fisse per codice, piu' una scritta dalla persona nel Profilo
 * (testo breve, ripulito da `rispostaRapidaPulita`).
 */
export const RISPOSTE_RAPIDE_TALK = {
    richiamo5: 'Ti richiamo tra 5 minuti',
    riunione: 'Sono in riunione',
    chat: 'Scrivimi in chat',
};
export const RISPOSTA_RAPIDA_MASSIMO = 120;
/**
 * Il testo di una risposta rapida: un codice delle frasi fisse diventa la sua frase, un testo libero si
 * ripulisce (via i caratteri di controllo e gli invisibili, spazi uniti, al piu' 120 caratteri). Vuoto o
 * non stringa: `null`, e il rifiuto resta un rifiuto semplice.
 */
export function rispostaRapidaPulita(dato) {
    if (typeof dato !== 'string')
        return null;
    if (Object.prototype.hasOwnProperty.call(RISPOSTE_RAPIDE_TALK, dato))
        return RISPOSTE_RAPIDE_TALK[dato];
    const invisibile = (c) => c < 0x20 || (c >= 0x7f && c <= 0x9f) || (c >= 0x200b && c <= 0x200f) || (c >= 0x2028 && c <= 0x202e) || (c >= 0x2060 && c <= 0x206f) || c === 0xfeff;
    const pulito = Array.from(dato)
        .map((ch) => (invisibile(ch.codePointAt(0) ?? 0) ? ' ' : ch))
        .join('')
        .replace(/\s+/g, ' ')
        .trim();
    if (pulito === '')
        return null;
    return Array.from(pulito).slice(0, RISPOSTA_RAPIDA_MASSIMO).join('').trim();
}
/** I tempi della richiamata su occupato. */
export const RICHIAMATA_TALK = {
    /** Dopo un tentativo non riuscito (non ha risposto), quanto si aspetta prima di riprovare. */
    pausaSecondi: 60,
    /** Quante volte si prova a mettere in contatto, poi ci si arrende. */
    tentativiMassimi: 3,
    /** Quante richiamate vive puo' avere una persona insieme. */
    viveMassime: 5,
};
/** I posti di parcheggio: «P1» … «P9». */
export const POSTI_PARCHEGGIO = 9;
/** Il testo di un posto di parcheggio: `P3`. */
export function nomePostoParcheggio(posto) {
    return `P${posto}`;
}
/**
 * Un numero **esterno** (Cosimo, 2026-11-02: *«il dialer deve potermi permettere di chiamare anche
 * gli esterni quindi non limitarlo a 3 numeri»*): cifre, eventualmente un `+` davanti, da 4 a 16. Le
 * chiamate verso l'esterno arrivano con il trunk (studi `spec/voip/1..6`): fino ad allora il server
 * riconosce il numero e risponde `esterne_non_attive`, non «interno inesistente».
 */
export const NUMERO_ESTERNO = /^\+?[0-9]{4,16}$/;
export function numeroEsternoValido(numero) {
    return typeof numero === 'string' && NUMERO_ESTERNO.test(numero);
}
/** I tempi e i limiti. Il limite di chiamate e la durata dello squillo si cambiano nelle impostazioni. */
export const TALK = {
    /** Quanto squilla prima di diventare «persa». */
    squilloSecondi: 30,
    /**
     * Ogni quanto si ripete la spinta ai telefoni che **non hanno ancora detto «sto squillando»**.
     * Una spinta persa (rete, Apple o Google in ritardo) non deve voler dire un telefono muto.
     */
    // 2,5 s e non 4 (Cosimo, 2/11 sera: «lo squillo deve arrivare subito»): una spinta persa per
    // strada costa al massimo due secondi e mezzo di silenzio, non quattro.
    ripetiSquilloMs: 2_500,
    /** Quante volte al massimo si ripete la spinta a un telefono, in uno squillo. */
    ripetizioniMassime: 10,
    /** Chiamate vive contemporanee in tutto il sistema (Cosimo: «Al massimo gestiamo 64 chiamate»). */
    limiteChiamate: 64,
    /** Persone al massimo in una chiamata, contando chi squilla ancora (la conferenza). */
    massimoPartecipanti: 8,
    /** Il registro delle chiamate e dei loro eventi si tiene tanto (studio legale). */
    conservazioneGiorni: 30,
    /** Una chiamata «in corso» con la stanza vuota da tanto si chiude da se'. */
    stanzaVuotaSecondi: 60,
    /**
     * Chi risulta dentro una chiamata ma non e' nella voce da tanto esce, come se avesse riagganciato (la
     * chiamata fantasma del 3/11: l'app si era persa la chiamata e la persona restava «occupata» per tutti).
     * Il motore si interroga ogni 10 s: l'uscita arriva fra 30 e 40 s.
     */
    assenteSecondi: 30,
    /** Il QR del Cruscotto vale tanto. */
    qrSecondi: 120,
    /** Tentativi sbagliati delle due cifre prima che il QR si bruci. */
    qrTentativi: 1,
    /** Il gettone del motore per entrare nella stanza: breve, si chiede a ogni ingresso. */
    gettoneSecondi: 300,
    /** Il prefisso delle stanze di Talk sul motore LiveKit. */
    prefissoStanza: 'talk-',
    /** Il bundle dell'app su iPhone (e il nome del pacchetto su Android). */
    bundle: 'it.beste.talk',
    /** Dopo la fine di una chiamata senza risposta, per quanto chi ha chiamato puo' lasciare un messaggio. */
    segreteriaFinestraSecondi: 600,
    /** Un messaggio in segreteria dura al massimo tanto. */
    segreteriaMassimoSecondi: 120,
    /** Il file di un messaggio pesa al massimo tanto (AAC a 32 kbit/s per due minuti stanno in ~0,5 MB). */
    segreteriaMassimoByte: 4 * 1024 * 1024,
    /** Un citofono a cui si e' risposto si chiude da se' dopo tanto (non ha voce: si apre e basta). */
    citofonoInCorsoSecondi: 120,
    /** Quanto squilla un annuncio dell'interfono a chi non risponde da solo. */
    annuncioSquilloSecondi: 20,
    /** Un annuncio dura al massimo tanto: l'interfono non e' una telefonata. */
    annuncioMassimoSecondi: 300,
    /**
     * La coda dei gruppi (4/11): chi ha lasciato squillare una chiamata in coda senza rispondere non la riceve di
     * nuovo per tanto, cosi' il telefono di chi e' lontano dalla scrivania non suona senza fine. Gli altri della
     * coda aspettano lo stesso: la piu' vecchia prima, sempre.
     */
    codaPausaSecondi: 6,
    /** L'attesa massima in coda che il Centralino lascia scegliere, in secondi (dalla chiamata). */
    attesaMassimaMinima: 30,
    attesaMassimaMassima: 1800,
};
/**
 * I codec della voce (Cosimo, 2026-11-02 sera: *«VOGLIO CHE TU USI I CODEC PIU AVANZATI E STABILI
 * POSSIBILE QUANDO SI PARLA TRA CELLULARI MENTRE TU USI QUELLI COMPATIBILI QUANDO PARLI CON I
 * CORDLESS»*). Una scelta sola, letta dall'app (pubblicazione del microfono) e dal ponte SIP verso i
 * cordless quando arrivera'.
 *
 * - **Fra cellulari**: **Opus** a 48 kHz, banda piena, mono, 32 kbit/s di obiettivo — la voce piena
 *   di un'app di chiamate moderna, a un ottavo della banda di un video. Per la tenuta su reti cattive
 *   (un capannone, il 4G in macchina): la **FEC** di Opus (ricostruisce dal pacchetto dopo), il **DTX**
 *   (nei silenzi non si manda quasi niente, e la rete resta libera per quando si parla) e il **bitrate
 *   a caldo** 32/24/16k secondo la linea (K882, K884). **Niente RED** (K884): raddoppia la voce, e
 *   sulle reti strette e' quel peso a fare la congestione. Sempre accese le
 *   tre lavorazioni del microfono: cancellazione dell'eco, riduzione del rumore, guadagno automatico.
 * - **Verso i cordless e i telefoni fissi** (il ponte SIP, studi `spec/voip/1..6`): **G.722** prima
 *   (la «voce HD» che le basi DECT del M900 e i fissi Yealink parlano), poi **G.711 A-law** (lo
 *   standard europeo che parla tutto), poi μ-law come ultimo ripiego; pacchetti da 20 ms e i toni dei
 *   tasti come eventi RFC 4733, non come suono.
 */
export const VOCE_TALK = {
    traCellulari: {
        codec: 'opus',
        frequenzaHz: 48_000,
        canali: 1,
        bitrateMassimo: 32_000,
        /**
         * Il bitrate **a caldo**, secondo la linea peggiore della chiamata (K882). Il server LiveKit
         * inoltra l'audio cosi' com'e', non lo ricodifica per chi ascolta: se chi ascolta ha una rete
         * stretta, a scendere deve essere chi parla. Misurato sul banco di prova (rete di chi ascolta a
         * 48 kbit/s, 5% di perdita, 150 ms ± 50): a 32 kbit/s fisso si perdeva un quarto delle sillabe.
         * Si scende subito, si risale dopo `risalitaMs` di qualita' stabile.
         *
         * I gradini vengono dalla serie C del banco (2/11, rete di chi parla, sillabe perse / buffer):
         * 4G debole 55,8% a 32k → 0% a 24k; 3G 100% a 32k → 0,4% e 34 ms a 16k (a 24k 241 ms, a 12k 331
         * ms). 12k non da' niente piu' di 16k e la voce peggiora: il gradino piu' basso e' 16k. Sotto i
         * ~32 kbit/s di rete il limite sono le intestazioni dei pacchetti, non Opus.
         */
        bitratePerQualita: { ottima: 32_000, buona: 24_000, scarsa: 16_000 },
        /**
         * La risalita: un gradino alla volta, dopo 20 s di linea migliore. Piu' svelta, il bitrate
         * risaliva, la rete stretta tornava a congestionarsi e la voce oscillava.
         */
        risalitaMs: 20_000,
        /**
         * Come si sente chi ascolta, misurato da lui su 4 s (audio ricostruito dal decodificatore e
         * buffer di jitter) e mandato a chi parla (`talk-linea`). Soglie dal banco: 3G a 32k = 40%
         * ricostruito e 640 ms; 4G debole = 21% e 136-230 ms; rete pulita = 0% e ~30 ms. «Scarsa» la decide
         * il solo buffer (congestione → 16k); l'audio ricostruito a buffer basso (perdite) porta a «buona».
         * Matrice del 2/11 (rete di chi parla, sillabe perse 32/24/16k): 15% perdita 0,8/0/0; raffiche
         * 5/3,3/7,9; 4G debole 9,6/0/0; 3G 18,9/10,1/4,6.
         */
        sogliaScarsa: { bufferMs: 250 },
        sogliaBuona: { ricostruitoPerCento: 2, bufferMs: 120 },
        /**
         * **RED spento** (K884, 2/11 sera; era acceso). RED manda ogni pacchetto anche nel successivo: il
         * doppio della voce. Sulle reti strette e' proprio il peso che fa la congestione — 32k con RED sono
         * ~64k di voce piu' ~20k di intestazioni, piu' di un 4G debole da 64k prima di qualunque guasto;
         * 16k con RED ~52k su un 3G da 48k. E il beneficio l'ha gia' il FEC di Opus: sul banco, senza RED,
         * 15% di perdite casuali = 0 parole perse; sulle raffiche (piu' pacchetti di fila) RED, che copre
         * un pacchetto alla volta, aiuta poco. Spento, i telefoni fanno quel che il banco ha misurato.
         */
        red: false,
        fec: true,
        dtx: true,
        cancellazioneEco: true,
        riduzioneRumore: true,
        guadagnoAutomatico: true,
    },
    versoCordless: {
        codec: ['G722', 'PCMA', 'PCMU'],
        pacchettoMs: 20,
        toniTasti: 'rfc4733',
    },
};
/** Tre cifre, `000`-`999`: «l'interno di 3 numeri come ho fatto fino ad adesso». */
export const NUMERO_INTERNO = /^[0-9]{3}$/;
export function numeroInternoValido(numero) {
    return typeof numero === 'string' && NUMERO_INTERNO.test(numero);
}
/**
 * Gli eventi del registro delle chiamate (`talk_call_events.tipo`). Un nome per fatto, non una
 * frase: si filtrano e si contano.
 */
export const EVENTI_TALK = [
    /** La chiamata e' nata. */
    'creata',
    /** Rifiutata prima di nascere: occupato, limite, interno spento. */
    'rifiutata_alla_nascita',
    /** Una spinta di squillo mandata a un telefono (con canale, esito, errore, millisecondi). */
    'spinta_squillo',
    /** La stessa spinta ripetuta perche' il telefono non aveva confermato. */
    'spinta_ripetuta',
    /** Il chiamato non ha telefoni capaci di squillare. */
    'nessun_telefono',
    /** Un telefono dice «sto squillando». */
    'squilla_sul_telefono',
    /** Un telefono dice che lo squillo non si e' potuto mostrare (permessi, sistema). */
    'squillo_fallito',
    'risposta',
    'rifiutata',
    'annullata',
    'persa',
    'conclusa',
    /** Un telefono e' entrato o uscito dalla stanza della voce (dichiarato dall'app). */
    'entrato_in_stanza',
    'uscito_dalla_stanza',
    /** La spinta che dice agli altri telefoni di smettere di squillare. */
    'spinta_fine_squillo',
    /** Chiusa dal sistema: stanza vuota, scadenza. */
    'chiusa_dal_sistema',
    /** Un problema segnalato dall'app (testo breve, mai dati personali). */
    'segnalazione_app',
    /** Qualcuno ha aggiunto una persona alla chiamata in corso (dettagli: chi). */
    'invitato',
    /** L'ospite ha risposto ed e' entrato. */
    'ospite_entrato',
    'invito_rifiutato',
    /** Lo squillo dell'ospite e' finito senza risposta. */
    'invito_perso',
    /** Chi l'aveva invitato l'ha ritirato, o la chiamata e' finita mentre squillava. */
    'invito_annullato',
    /** Una persona e' uscita e la chiamata e' continuata per gli altri. */
    'uscito',
    /** Una persona ha messo in attesa la chiamata (non la sentono e non sente), e l'ha ripresa. */
    'in_attesa',
    'ripresa',
    /** Il trasferimento (dettagli: modo, da, verso): avviato, completato, annullato, non riuscito. */
    'passaggio_avviato',
    'passaggio_completato',
    'passaggio_annullato',
    'passaggio_fallito',
    /** La chiamata torna a chi l'aveva passata o parcheggiata, perche' nessuno l'ha presa. */
    'ritorno',
    /** Una persona esclusa dalla voce dal server durante un consulto o un parcheggio, e riammessa. */
    'tenuto',
    'rilasciato',
    /** Il parcheggio: messa al posto P1…P9, ripresa da qualcuno, scaduta. */
    'parcheggiata',
    'ripresa_dal_parcheggio',
    'parcheggio_scaduto',
    /** La chiamata seguita verso un altro numero (dettagli: perche' — sempre, occupato, non risponde). */
    'inoltrata',
    /** Il chiamato ha il non disturbare acceso. */
    'non_disturbare',
    /** In una chiamata di gruppo: il posto di un membro ha smesso di squillare perche' ha risposto un altro. */
    'risposto_altrove',
    /** La segreteria: il messaggio lasciato, e la sua trascrizione. */
    'segreteria_lasciata',
    'segreteria_trascritta',
    /** Il citofono: chi ha risposto ha aperto la porta (dettagli: esito del comando alla centralina). */
    'porta_aperta',
    /** La richiamata su occupato: prenotata, partita verso chi si e' liberato, riuscita, non riuscita. */
    'richiamata_prenotata',
    'richiamata_avviata',
    'richiamata_riuscita',
    'richiamata_fallita',
    /** La coda dei gruppi: la chiamata aspetta (nessuno a cui squillare), e squilla dalla coda a chi si e' liberato. */
    'in_coda',
    'squillo_dalla_coda',
];
const ORDINALI = ['primo', 'secondo', 'terzo', 'quarto', 'quinto', 'sesto', 'settimo', 'ottavo', 'nono', 'decimo'];
/** «Sei il secondo in attesa»: la posizione nella coda come la legge chi chiama. */
export function fraseCoda(posizione) {
    if (!Number.isInteger(posizione) || posizione < 1)
        return 'Sei in attesa';
    return posizione <= ORDINALI.length ? `Sei il ${ORDINALI[posizione - 1]} in attesa` : `Sei il numero ${posizione} in attesa`;
}
/** Quanto, in «m:ss» («1:20»): l'attesa di chi chiama, per chi risponde. */
export function durataAttesa(secondi) {
    const s = Math.max(0, Math.floor(secondi));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
/**
 * Il nome di una persona come lo mostra Beste Talk: **iniziale maiuscola e basta** (Cosimo,
 * 2026-11-02: *«Nome e cognome mettilo con la iniziale maiuscola e basta e non tutto maiuscolo»*).
 * Il gestionale li scrive tutti maiuscoli («PUGGELLI FRANCESCO»): qui diventano «Puggelli Francesco».
 * Ogni parola ha la sua maiuscola, anche dopo un apostrofo o un trattino («D'ANGELO» → «D'Angelo»,
 * «ROSSI-BIANCHI» → «Rossi-Bianchi»). L'ordine delle parole resta quello dell'anagrafica.
 */
export function nomeDiPersona(testo) {
    if (!testo)
        return '';
    return testo
        .trim()
        .replace(/\s+/g, ' ')
        .toLocaleLowerCase('it-IT')
        .replace(/(^|[\s'’-])(\p{L})/gu, (_m, prima, lettera) => prima + lettera.toLocaleUpperCase('it-IT'));
}
//# sourceMappingURL=talk.js.map